// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAuth } from '../context/AuthContext';

// Pre-logout inactivity warning (added 2026-06-16 per user request). This is a NET-NEW feature
// beyond the original Python+React app, which only showed a banner AFTER logout. It listens for
// the 'session-inactivity-warning' event (carrying an absolute logout `deadline`) emitted by
// AuthContext and runs its OWN countdown to that deadline so the timer visibly ticks down every
// second regardless of how AuthContext is scheduled. "Stay signed in" resets the activity clock,
// refreshes the token, and emits 'session-stay' to cancel the latched deadline. If the countdown
// reaches zero the modal itself fires 'session-timeout' to guarantee the logout actually happens.
const SessionWarningModal = () => {
  const { updateActivity, refreshToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [deadline, setDeadline] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    const onWarn = (e) => {
      setDeadline(e.detail?.deadline ?? null);
      setOpen(true);
    };
    const onClear = () => {
      setOpen(false);
      setDeadline(null);
      setSecondsLeft(null);
    };
    window.addEventListener('session-inactivity-warning', onWarn);
    window.addEventListener('session-inactivity-clear', onClear);
    window.addEventListener('session-timeout', onClear);
    window.addEventListener('user-logout', onClear);
    return () => {
      window.removeEventListener('session-inactivity-warning', onWarn);
      window.removeEventListener('session-inactivity-clear', onClear);
      window.removeEventListener('session-timeout', onClear);
      window.removeEventListener('user-logout', onClear);
    };
  }, []);

  // Drive the visible countdown ourselves from the absolute deadline. Ticking at 250ms keeps the
  // displayed seconds accurate and makes the eventual logout prompt. When we hit zero we dispatch
  // 'session-timeout' so the user is actually signed out (handleSessionTimeout is idempotent).
  useEffect(() => {
    if (!open || deadline == null) return undefined;
    let firedLogout = false;
    const update = () => {
      const ms = deadline - Date.now();
      const s = Math.max(0, Math.ceil(ms / 1000));
      setSecondsLeft(s);
      if (ms <= 0 && !firedLogout) {
        firedLogout = true;
        window.dispatchEvent(new Event('session-timeout'));
      }
    };
    update();
    const id = setInterval(update, 250);
    return () => clearInterval(id);
  }, [open, deadline]);

  const handleStay = useCallback(() => {
    setOpen(false);
    setDeadline(null);
    setSecondsLeft(null);
    updateActivity(); // reset the inactivity clock
    refreshToken(); // proactively renew the JWT
    window.dispatchEvent(new Event('session-stay')); // cancel the latched logout deadline
  }, [updateActivity, refreshToken]);

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      aria-labelledby="session-warning-title"
      // Sit above fullscreen remote viewers (HttpViewer / Guacamole overlays).
      sx={{ zIndex: (theme) => theme.zIndex.modal + 20 }}
    >      <DialogTitle id="session-warning-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon color="warning" />
          Are you still there?
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          You've been inactive, so you'll be signed out
          {secondsLeft != null ? ' in ' : ' shortly'}
          {secondsLeft != null && (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                minWidth: '2.2em',
                textAlign: 'center',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                color: secondsLeft <= 10 ? 'error.main' : 'warning.main',
              }}
            >
              {secondsLeft}
            </Box>
          )}
          {secondsLeft != null && ` second${secondsLeft === 1 ? '' : 's'}`}. Choose "Stay signed in"
          to continue your session.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleStay} variant="contained" autoFocus>
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionWarningModal;
